import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Chip,
  Box,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import {
  Business as BusinessIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Add as AddIcon
} from '@mui/icons-material';

const DepartmentFilter = ({ departments, selectedDepartments, onDepartmentChange }) => {
  const theme = useTheme();
  const [departmentSelectionDialogOpen, setDepartmentSelectionDialogOpen] = useState(false);
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const [tempSelectedDepartments, setTempSelectedDepartments] = useState([]);

  // Get flattened departments for dropdown
  const getFlatDepartments = (depts, level = 0) => {
    let result = [];
    depts.forEach(dept => {
      result.push({
        ...dept,
        displayName: '  '.repeat(level) + dept.name,
        level
      });
      if (dept.children?.length) {
        result = result.concat(getFlatDepartments(dept.children, level + 1));
      }
    });
    return result;
  };

  const flatDepartments = getFlatDepartments(departments);

  const handleOpenDepartmentSelectionDialog = () => {
    setTempSelectedDepartments([...selectedDepartments]);
    setDepartmentSelectionDialogOpen(true);
    setDepartmentSearchTerm('');
  };

  const handleCloseDepartmentSelectionDialog = () => {
    setDepartmentSelectionDialogOpen(false);
    setDepartmentSearchTerm('');
    setTempSelectedDepartments([]);
  };

  const handleDepartmentToggle = (departmentId) => {
    setTempSelectedDepartments(prev => 
      prev.includes(departmentId)
        ? prev.filter(id => id !== departmentId)
        : [...prev, departmentId]
    );
  };

  const handleConfirmDepartmentSelection = () => {
    onDepartmentChange(tempSelectedDepartments);
    setDepartmentSelectionDialogOpen(false);
    setDepartmentSearchTerm('');
    setTempSelectedDepartments([]);
  };

  const getFilteredDepartmentsForSelection = () => {
    if (!departmentSearchTerm) return flatDepartments || [];
    
    const searchLower = departmentSearchTerm.toLowerCase();
    return (flatDepartments || []).filter(department => 
      department.name.toLowerCase().includes(searchLower)
    );
  };

  const handleChipDelete = (departmentId) => {
    onDepartmentChange(selectedDepartments.filter(id => id !== departmentId));
  };

  const getDepartmentName = (id) => {
    const dept = departments.find(d => d.id === id);
    return dept ? dept.name : 'Unknown';
  };

  return (
    <>
      <Paper sx={{ 
        p: 3, 
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.grey[50], 0.5)})`,
        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`,
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterListIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
            Filter by Department
          </Typography>
        </Box>

        <Button
          fullWidth
          variant="outlined"
          onClick={handleOpenDepartmentSelectionDialog}
          sx={{
            justifyContent: 'flex-start',
            textAlign: 'left',
            py: 1,
            px: 2,
            height: 40,
            borderRadius: 2,
            backgroundColor: '#f8f9fa',
            border: `1px solid rgba(0,0,0,0.1)`,
            color: selectedDepartments.length > 0 ? 'text.primary' : 'text.secondary',
            fontWeight: 500,
            textTransform: 'none',
            fontSize: '0.875rem',
            mb: 2,
            '&:hover': {
              border: `1px solid rgba(0,0,0,0.2)`,
              backgroundColor: '#f8f9fa'
            }
          }}
        >
          {selectedDepartments.length === 0 
            ? 'Select Departments...'
            : `${selectedDepartments.length} department${selectedDepartments.length > 1 ? 's' : ''} selected`
          }
        </Button>

        {/* Selected Department Chips */}
        {selectedDepartments.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedDepartments.map((departmentId) => (
              <Chip
                key={departmentId}
                label={getDepartmentName(departmentId)}
                onDelete={() => handleChipDelete(departmentId)}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: 'primary.main'
                  }
                }}
              />
            ))}
          </Box>
        )}

        {selectedDepartments.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            All departments shown
          </Typography>
        )}
      </Paper>

      {/* Department Selection Dialog - Exact copy from WorkTab.jsx */}
      <Dialog
        open={departmentSelectionDialogOpen}
        onClose={handleCloseDepartmentSelectionDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '40vh',
            width: '600px',
            maxWidth: '600px',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          fontSize: '1.25rem', 
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Select Departments
          </Typography>
          <IconButton onClick={handleCloseDepartmentSelectionDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search departments..."
              value={departmentSearchTerm}
              onChange={(e) => setDepartmentSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Department</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredDepartmentsForSelection().map((department) => (
                  <TableRow 
                    key={department.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2,
                        pl: 3,
                        pr: 3
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {department.displayName}
                        </Typography>
                        {department.description && (
                          <Typography variant="caption" color="text.secondary">
                            {department.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDepartmentToggle(department.id)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          bgcolor: tempSelectedDepartments.includes(department.id) ? 'black' : 'transparent',
                          color: tempSelectedDepartments.includes(department.id) ? 'white' : 'inherit',
                          '&:hover': {
                            bgcolor: tempSelectedDepartments.includes(department.id) ? 'black' : 'transparent',
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          }
                        }}
                      >
                        {tempSelectedDepartments.includes(department.id) ? 'Selected' : 'Select'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {getFilteredDepartmentsForSelection().length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {departmentSearchTerm ? 'No departments found matching your search.' : 'No departments available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Typography variant="body2" color="text.secondary">
            {tempSelectedDepartments.length} of {departments.length} selected
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleCloseDepartmentSelectionDialog}
              variant="outlined"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none'
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDepartmentSelection}
              variant="contained"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                background: 'black',
                color: 'white',
                border: '1px solid black',
                boxShadow: `0 4px 12px ${alpha('#000', 0.3)}`,
                '&:hover': {
                  background: 'black',
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 16px ${alpha('#000', 0.4)}`
                }
              }}
            >
              Apply Filter
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DepartmentFilter;